import { useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import ObrasApi from 'Data/Obras.api';
import { FILES_QUERY_KEY, QUERY_KEY_MOVE_FILES_LIST } from 'Data/keys/QueryKeys';
import { IUseQueryFilesReturn } from 'Data/interfaces/ListagemArquivos/IListFiles';
import { IUpdateFilesStatusRequest } from 'Data/interfaces/Obra/IUpdateFilesStatusRequest';
import { FederatedViewerActions } from 'Store/FederatedViewer/FederatedViewer.actions';
import { UploadActions } from 'Store/Upload/Upload.actions';
import { queryClient } from 'Context/QueryClientContext';
import toastHandler from 'Utils/toastHandler';

export const useQueryUpdateFilesStatus = () => {
  const dispatch = useDispatch();

  const { mutate, isPending } = useMutation({
    mutationFn: (variables: IUpdateFilesStatusRequest) => {
      return ObrasApi.updateFilesStatus(variables);
    },
    onSuccess: async ({ data }, variables) => {
      const constructionSiteId = Number(variables.constructionSiteFk);
      
      await Promise.all([
        await queryClient.cancelQueries({
          queryKey: [
            FILES_QUERY_KEY,
            constructionSiteId,
            variables.folderId,
          ],
        }),
        await queryClient.cancelQueries({
          queryKey: [
            QUERY_KEY_MOVE_FILES_LIST,
            constructionSiteId,
            variables.folderId,
          ],
        }),
      ]);

      queryClient.setQueryData<IUseQueryFilesReturn>(
        [FILES_QUERY_KEY, constructionSiteId, variables.folderId],
        (old) => {
          if (old) {
            return {
              ...old,
              ListFiles: old.ListFiles.map((file) => {
                const index = data.data.findIndex((info) => {
                  return info.ApiId === file.Identifier.ApiId;
                });
                if (index !== -1) {
                  return {
                    ...file,
                    FileInfo: {
                      ...file.FileInfo,
                      Status: data.data[index].Status,
                    },
                    Status: data.data[index].Status,
                  };
                }
                return file;
              }),
            };
          }
          return old;
        },
      );

      queryClient.setQueryData<IUseQueryFilesReturn>(
        [
          QUERY_KEY_MOVE_FILES_LIST,
          constructionSiteId,
          variables.folderId,
        ],
        (old) => {
          if (old) {
            return {
              ...old,
              ListFiles: old.ListFiles.map((file) => {
                const index = data.data.findIndex((info) => {
                  return info.ApiId === file.Identifier.ApiId;
                });
                if (index !== -1) {
                  return {
                    ...file,
                    FileInfo: {
                      ...file.FileInfo,
                      Status: data.data[index].Status,
                    },
                    Status: data.data[index].Status,
                  };
                }
                return file;
              }),
            };
          }
          return old;
        },
      );

      // Update store for upload and viewer
      const updatedFiles = data.data.map(item => ({
        ApiId: item.ApiId,
        Status: item.Status,
        ConstructionSiteFk: item.ConstructionSiteFk,
        ConstructionSiteDisciplineFk: item.ConstructionSiteDisciplineFk,
        ApiFolderId: item.ApiFolderId,
      }));

      dispatch(UploadActions.updateFilesStatus(updatedFiles.map(f => ({ ApiId: f.ApiId, Status: f.Status }))));
      dispatch(FederatedViewerActions.updateFileViewerInfo(updatedFiles as any));

      // If cache was undefined, invalidate to trigger refetch
      const cacheData = queryClient.getQueryData([FILES_QUERY_KEY, constructionSiteId, variables.folderId]);
      if (!cacheData) {
        await queryClient.invalidateQueries({
          queryKey: [FILES_QUERY_KEY, constructionSiteId, variables.folderId],
        });
      }

      const onlyOne = variables.fileApiIds.length === 1 ? '' : 's';
      toastHandler.showSuccess(
        `Status do${onlyOne} arquivo${onlyOne} alterado${onlyOne} com sucesso!`
      );
    },
    onError: async (error: any) => {
      const errorMsg = error.errors
        ? error.errors[0].Message
        : 'Erro ao alterar status dos arquivos';
      toastHandler.showError(errorMsg);
    },
  });

  return {
    updateFilesStatus: mutate,
    isLoading: isPending,
  };
};
